/**
 * Media Cache Service
 * Implements browser-based caching for media resources using IndexedDB and memory cache
 */

import { mediaConfig } from '@/config/media'

class MediaCacheService {
  constructor() {
    this.memoryCache = new Map()
    this.maxMemoryCacheSize = mediaConfig.cache.memoryCache.maxSize
    this.currentMemorySize = 0
    this.dbName = mediaConfig.cache.indexedDB.dbName
    this.dbVersion = mediaConfig.cache.indexedDB.dbVersion
    this.storeName = mediaConfig.cache.indexedDB.storeName
    this.db = null
    this.initPromise = this.initDB()
  }

  async initDB() {
    if (!('indexedDB' in window)) {
      console.warn('IndexedDB not supported, using memory cache only')
      return
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'url' })
          store.createIndex('timestamp', 'timestamp', { unique: false })
          store.createIndex('size', 'size', { unique: false })
        }
      }
    })
  }

  async get(url) {
    // Check memory cache first
    if (this.memoryCache.has(url)) {
      const cached = this.memoryCache.get(url)
      // Move to end (LRU)
      this.memoryCache.delete(url)
      this.memoryCache.set(url, cached)
      return cached.blob
    }

    // Check IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction([this.storeName], 'readonly')
        const store = transaction.objectStore(this.storeName)
        const request = store.get(url)

        return new Promise((resolve, reject) => {
          request.onsuccess = () => {
            const result = request.result
            if (result && result.blob) {
              // Add to memory cache
              this.addToMemoryCache(url, result.blob, result.metadata)
              resolve(result.blob)
            } else {
              resolve(null)
            }
          }
          request.onerror = () => reject(request.error)
        })
      } catch (error) {
        console.error('Error reading from IndexedDB:', error)
        return null
      }
    }

    return null
  }

  async set(url, blob, metadata = {}) {
    await this.initPromise

    // Add to memory cache
    this.addToMemoryCache(url, blob, metadata)

    // Add to IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction([this.storeName], 'readwrite')
        const store = transaction.objectStore(this.storeName)
        
        const data = {
          url,
          blob,
          metadata,
          timestamp: Date.now(),
          size: blob.size
        }

        const request = store.put(data)
        
        return new Promise((resolve, reject) => {
          request.onsuccess = () => resolve()
          request.onerror = () => {
            console.error('Failed to cache in IndexedDB:', request.error)
            reject(request.error)
          }
        })
      } catch (error) {
        console.error('Error writing to IndexedDB:', error)
      }
    }
  }

  addToMemoryCache(url, blob, metadata) {
    const size = blob.size

    // Remove items if cache is full (LRU)
    while (this.currentMemorySize + size > this.maxMemoryCacheSize && this.memoryCache.size > 0) {
      const firstKey = this.memoryCache.keys().next().value
      const firstItem = this.memoryCache.get(firstKey)
      this.currentMemorySize -= firstItem.blob.size
      this.memoryCache.delete(firstKey)
    }

    // Add new item
    this.memoryCache.set(url, { blob, metadata })
    this.currentMemorySize += size
  }

  async fetch(url, options = {}) {
    // Try to get from cache first
    const cached = await this.get(url)
    if (cached && !options.forceRefresh) {
      return cached
    }

    // Fetch from network
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      const metadata = {
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length'),
        lastModified: response.headers.get('last-modified'),
        etag: response.headers.get('etag')
      }

      // Cache the result
      await this.set(url, blob, metadata)

      return blob
    } catch (error) {
      console.error('Failed to fetch media:', error)
      // Return cached version if available
      return cached
    }
  }

  async preload(urls) {
    const promises = urls.map(url => this.fetch(url))
    return Promise.allSettled(promises)
  }

  async clear() {
    // Clear memory cache
    this.memoryCache.clear()
    this.currentMemorySize = 0

    // Clear IndexedDB
    if (this.db) {
      const transaction = this.db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      store.clear()
    }
  }

  async getSize() {
    let totalSize = this.currentMemorySize

    if (this.db) {
      const transaction = this.db.transaction([this.storeName], 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.openCursor()

      return new Promise((resolve) => {
        request.onsuccess = (event) => {
          const cursor = event.target.result
          if (cursor) {
            totalSize += cursor.value.size
            cursor.continue()
          } else {
            resolve(totalSize)
          }
        }
        request.onerror = () => resolve(totalSize)
      })
    }

    return totalSize
  }
}

// Create singleton instance
export const mediaCache = new MediaCacheService()

// Helper function to create object URL from cached blob
export async function getCachedMediaUrl(url) {
  const blob = await mediaCache.fetch(url)
  if (blob) {
    return URL.createObjectURL(blob)
  }
  return url
}